<template>
  <form class="modeling-form" @submit.prevent="onSubmit">
    <h3 class="form-title">Cuéntanos qué necesitas</h3>

    <label class="block">
      <span>Descripción de la pieza o producto</span>
      <textarea
        v-model="form.description"
        rows="6"
        :minlength="MIN_CHARS"
        :maxlength="MAX_CHARS"
        required
        placeholder="Describe el objeto que necesitas: forma, función, referencias, materiales pensados, contexto de uso…"
      ></textarea>
      <small class="counter" :class="{ 'is-low': descriptionLength < MIN_CHARS }" aria-live="polite">
        {{ descriptionLength }} / {{ MAX_CHARS }} caracteres
        ({{ descriptionLength < MIN_CHARS ? `mínimo ${MIN_CHARS}` : 'OK' }})
      </small>
    </label>

    <fieldset>
      <legend>Dimensiones aproximadas (opcional)</legend>
      <div class="dim-grid">
        <label>
          <span>Largo (mm)</span>
          <input v-model.number="form.dimX" type="number" min="0" step="1" />
        </label>
        <label>
          <span>Ancho (mm)</span>
          <input v-model.number="form.dimY" type="number" min="0" step="1" />
        </label>
        <label>
          <span>Alto (mm)</span>
          <input v-model.number="form.dimZ" type="number" min="0" step="1" />
        </label>
      </div>
    </fieldset>

    <label class="block">
      <span>Cantidad estimada (opcional)</span>
      <input v-model.number="form.quantity" type="number" min="1" step="1" />
    </label>

    <h3 class="form-title">Datos de contacto</h3>

    <label class="block">
      <span>Nombre completo</span>
      <input v-model.trim="form.contactName" type="text" required />
    </label>

    <label class="block">
      <span>Correo electrónico</span>
      <input v-model.trim="form.contactEmail" type="email" required />
    </label>

    <label class="block">
      <span>Teléfono (opcional)</span>
      <input v-model.trim="form.contactPhone" type="tel" />
    </label>

    <button type="submit" class="button btn--block submit-btn" :disabled="!isValid || submitting">
      {{ submitting ? 'Enviando…' : 'Solicitar cotización' }}
    </button>

    <p class="legal-note">
      Al enviar tu solicitud aceptas nuestra
      <router-link to="/privacidad">política de privacidad</router-link>.
    </p>

    <p v-if="successMessage" class="ok-msg" role="status">{{ successMessage }}</p>
    <p v-if="errorMessage" class="err-msg" role="alert">{{ errorMessage }}</p>
  </form>
</template>

<script>
import { submitModelingRequest } from '@/services/print3dService';

const MIN_CHARS = 30;
const MAX_CHARS = 1000;
const DRAFT_KEY = 'print3d:modeling_draft';

const initialForm = () => ({
  description: '',
  dimX: null,
  dimY: null,
  dimZ: null,
  quantity: null,
  contactName: '',
  contactEmail: '',
  contactPhone: '',
});

export default {
  name: 'ModelingRequestForm',
  props: {
    hasAttachment: { type: Boolean, default: false },
    attachment: { type: File, default: null },
  },
  emits: ['submitted'],
  data() {
    return {
      MIN_CHARS,
      MAX_CHARS,
      form: initialForm(),
      submitting: false,
      successMessage: '',
      errorMessage: '',
    };
  },
  computed: {
    descriptionLength() {
      return this.form.description.length;
    },
    isValid() {
      const f = this.form;
      return (
        this.hasAttachment &&
        f.description.length >= MIN_CHARS &&
        f.description.length <= MAX_CHARS &&
        f.contactName.length > 0 &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.contactEmail)
      );
    },
  },
  watch: {
    form: {
      handler(val) {
        try {
          sessionStorage.setItem(DRAFT_KEY, JSON.stringify(val));
        } catch (_e) { /* sessionStorage puede no estar disponible */ }
      },
      deep: true,
    },
  },
  mounted() {
    try {
      const saved = sessionStorage.getItem(DRAFT_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        this.form = { ...initialForm(), ...parsed };
      }
    } catch (_e) { /* ignore */ }
  },
  methods: {
    async onSubmit() {
      if (!this.isValid) return;
      this.submitting = true;
      this.successMessage = '';
      this.errorMessage = '';
      try {
        const dims =
          this.form.dimX && this.form.dimY && this.form.dimZ
            ? { x: this.form.dimX, y: this.form.dimY, z: this.form.dimZ }
            : null;

        const { receipt, error } = await submitModelingRequest({
          description: this.form.description,
          dimensionsMm: dims,
          quantity: this.form.quantity || 1,
          contactName: this.form.contactName,
          contactEmail: this.form.contactEmail,
          contactPhone: this.form.contactPhone,
          file: this.attachment,
        });
        if (error) throw new Error(error);

        this.successMessage = `Solicitud recibida (#${receipt.id}). Te contactaremos al correo ${this.form.contactEmail} con la propuesta de modelado e impresión.`;
        this.$emit('submitted', receipt);
        this.form = initialForm();
        try { sessionStorage.removeItem(DRAFT_KEY); } catch (_e) { /* ignore */ }
      } catch (e) {
        this.errorMessage = e?.message || 'No se pudo enviar la solicitud.';
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.modeling-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: white;
  border: 1px solid #9DB2BF;
  border-radius: 12px;
  padding: 1.2rem;
}

.form-title {
  font-family: "Inter", sans-serif;
  font-size: 1.05rem;
  color: #111827;
  margin: 0.4rem 0 0;
}

form input, form select {
  margin-bottom: 0 !important;
}

fieldset {
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  padding: 0.8rem;
  margin: 0;
}

fieldset legend {
  padding: 0 0.4rem;
  color: #4F5665;
  font-size: 0.9rem;
}

.dim-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.6rem;
}

.dim-grid label,
.block {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.85rem;
  color: #4F5665;
}

input[type="number"],
input[type="text"],
input[type="email"],
input[type="tel"],
textarea {
  border: 1px solid #0B132A;
  border-radius: 6px;
  padding: 0.55rem 0.7rem;
  font-size: 16px;
  font-family: inherit;
  color: #2C3E50;
  background: white;
  resize: vertical;
}

input:focus,
textarea:focus {
  outline: 2px solid #526D82;
  outline-offset: 1px;
}

.counter {
  font-size: 0.78rem;
  color: #4F5665;
}

.counter.is-low {
  color: #B45309;
}

.submit-btn {
  background: #526D82;
  color: white;
  border: none;
  padding: 0.7rem 1rem;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.2s;
}

.submit-btn:hover:not(:disabled) {
  background: #3D566B;
}

.submit-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.ok-msg {
  background: rgba(22, 163, 74, 0.1);
  color: #166534;
  padding: 0.6rem 0.8rem;
  border-radius: 6px;
  margin: 0;
  font-size: 0.9rem;
}

.err-msg {
  background: rgba(185, 28, 28, 0.08);
  color: #991B1B;
  padding: 0.6rem 0.8rem;
  border-radius: 6px;
  margin: 0;
  font-size: 0.9rem;
}

@media (max-width: 800px) {
  .dim-grid {
    grid-template-columns: 1fr 1fr;
  }
}

.legal-note {
  font-size: 0.8rem;
  color: #4F5665;
  margin: 0;
  text-align: center;
}

.legal-note a {
  color: #526D82;
}
</style>
